import { FC, PropsWithChildren, useEffect } from 'react';
import { Stack, styled, SxProps, Theme, Typography } from '@mui/material';
import { BORDER_RADIUS_M, FontFamilies, FontWeights, Icon, useTimeLeft } from 'shared';

type TimerProps = {
  title: string;
  date: number;
  onTimerEnds?: () => void;
  sx?: SxProps<Theme>;
};

const TimeTypography = styled(Typography)(({ theme }) => ({
  fontSize: '40px',
  lineHeight: '40px',
  color: theme.themeColors.colorTimer,
  fontWeight: FontWeights.Regular,
  fontFamily: FontFamilies.secondary,
}));

const TimeBlock = styled(Stack)(({ theme }) => ({
  background: theme.themeColors.colorTimerBackground,
  width: '72px',
  height: '69px',
  borderRadius: BORDER_RADIUS_M,
}));

export const Timer: FC<TimerProps & PropsWithChildren> = ({ title, date, sx, onTimerEnds, children }) => {
  const timeLeft = useTimeLeft(date * 1000, true);
  useEffect(() => {
    if (
      timeLeft?.days === '00' &&
      timeLeft?.hours === '00' &&
      timeLeft?.minutes === '00' &&
      timeLeft?.seconds === '00'
    ) {
      onTimerEnds?.();
    }
  }, [timeLeft?.days, timeLeft?.hours, timeLeft?.minutes, onTimerEnds, timeLeft?.seconds]);
  if (!timeLeft) return <>-</>;
  const { days, hours, minutes } = timeLeft;

  return (
    <Stack
      sx={{
        width: '100%',
        ...sx,
      }}
      alignItems="center"
      spacing={1}
    >
      <Typography variant="button" fontWeight={FontWeights.Medium}>
        {title}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        {+days >= 0 && (
          <TimeBlock alignItems="center" justifyContent="center">
            <TimeTypography>{days}</TimeTypography>
            <Typography variant="caption">Days</Typography>
          </TimeBlock>
        )}
        <Icon type="colon" width={7} height={10} />
        {+hours >= 0 && (
          <TimeBlock alignItems="center" justifyContent="center">
            <TimeTypography>{hours}</TimeTypography>
            <Typography variant="caption">Hours</Typography>
          </TimeBlock>
        )}
        <Icon type="colon" width={7} height={10} />
        {+minutes >= 0 && (
          <TimeBlock alignItems="center" justifyContent="center">
            <TimeTypography>{minutes}</TimeTypography>
            <Typography variant="caption">Minutes</Typography>
          </TimeBlock>
        )}
      </Stack>
      {children}
    </Stack>
  );
};
